import React, { useEffect, useMemo, useCallback, useRef, useState } from 'react'
import AssigneePopover from 'components/AssigneePopover/AssigneePopover'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { getTaskCNsByProject, updateTaskCN, setSelectedTask, loadTemplateCN, checkDeletedDuplicateTasks, restoreTaskCN, reorderTaskCNs } from 'slices/taskCNSlice'
import { initializeTasks } from 'slices/tasksSlice'
import { initializeTemplates, buildTemplateFromTasks } from 'slices/templateSlice'
import { patchLocation } from 'slices/locationSlice'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Select } from 'components/ui/select'
import { Switch } from 'components/ui/switch'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from 'components/ui/table'
import { toast } from 'components/ui/toast-store'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import Popup from 'components/Popup'
import TaskTemplatesSelectCN from 'components/TaskTemplatesSelectCN'
import BuildTemplateModal from 'components/popups/BuildTemplateModal'
import TasksTimeline from 'components/TasksTimeline'
import TasksCalendar from 'components/TasksCalendar'
import CreateTaskCNModal from 'screens/taskscn/CreateTaskCNModal'
import TaskCNDetailSheet from 'screens/taskscn/TaskCNDetailSheet'
import SortableTasksTableBody from 'components/TasksList/SortableTasksTableBody'
import { TableSortIcon } from 'components/TableSortIcon'
import { TablePagination } from 'components/ui/pagination'
import useIsMobile from 'hooks/useIsMobile'
import { formatScheduledTime, filterTasks, sortTaskList, collectAssignees, isOverdue } from 'utils/tasks'
import { canCompleteTask } from 'utils/taskPermissions'
import { cn } from 'lib/utils'
import {
	Plus,
	Search,
	Filter,
	Check,
	Eye,
	Calendar,
	Download,
	Table2,
	GitBranch,
	LayoutTemplate,
	ArrowRight,
} from 'lucide-react'
import notify from 'components/ui/notify'
import DeletedDuplicateDialog from 'components/popups/DeletedDuplicateDialog'

// ─── Shared helpers (same as TasksCN_Index) ───────────────────────────────────

const PRIORITY_STYLES: Record<string, { text: string; bg: string; border: string }> = {
	'P1 - Urgent': { text: 'rgb(239, 68, 68)', bg: 'rgba(239, 68, 68, 0.1)', border: 'rgba(239, 68, 68, 0.2)' },
	'P2 - High': { text: 'rgb(249, 115, 22)', bg: 'rgba(249, 115, 22, 0.1)', border: 'rgba(249, 115, 22, 0.2)' },
	'P3 - Medium': { text: 'rgb(62, 124, 177)', bg: 'rgba(62, 124, 177, 0.1)', border: 'rgba(62, 124, 177, 0.2)' },
	'P4 - Low': { text: 'rgb(113, 113, 122)', bg: 'rgba(113, 113, 122, 0.1)', border: 'rgba(113, 113, 122, 0.2)' },
}

function PriorityBadge({ priority }: { priority: string }) {
	if (!priority) return <span className="text-gray-400">-</span>
	const label = priority.split(' - ')[0] || priority
	const style = PRIORITY_STYLES[priority]
	if (!style) return <span className="text-gray-400">{label}</span>
	return (
		<span
			className="inline-flex items-center justify-center px-2 py-0.5 rounded-md text-xs font-semibold border"
			style={{ color: style.text, backgroundColor: style.bg, borderColor: style.border }}
		>
			{label}
		</span>
	)
}

function EffortBadge({ effort }: { effort: string }) {
	if (!effort) return <span className="text-gray-400">-</span>
	const label = effort.split(' - ')[0] || effort
	return <span className="text-sm text-gray-500">{label}</span>
}

function formatDueDate(dateStr: string | null): string {
	if (!dateStr) return '-'
	const d = new Date(dateStr)
	const taskDate = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
	const today = new Date()
	today.setHours(0, 0, 0, 0)
	const tomorrow = new Date(today)
	tomorrow.setDate(tomorrow.getDate() + 1)
	if (taskDate.getTime() === today.getTime()) return 'Today'
	if (taskDate.getTime() === tomorrow.getTime()) return 'Tomorrow'
	return taskDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function TaskCheckbox({
	checked,
	onChange,
	disabled = false,
	title,
}: {
	checked: boolean
	onChange: () => void
	disabled?: boolean
	title?: string
}) {
	return (
		<button
			type="button"
			disabled={disabled}
			title={title}
			onClick={e => {
				e.stopPropagation()
				onChange()
			}}
			className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
				checked ? 'bg-[#0080fe] border-[#0080fe] text-white' : 'bg-transparent border-gray-300 hover:border-[#0080fe]'
			} ${disabled ? 'opacity-40 cursor-not-allowed hover:border-gray-300' : ''}`}
		>
			{checked && <Check className="w-2.5 h-2.5" />}
		</button>
	)
}

function EmptyState({ onCreateClick }: { onCreateClick: () => void }) {
	return (
		<div className="flex flex-col items-center justify-center py-16">
			<div className="w-16 h-16 rounded-full flex items-center justify-center mb-4 bg-gray-100">
				<svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={1.5}
						d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
					/>
				</svg>
			</div>
			<h2 className="text-lg font-semibold text-gray-900 mb-1">No tasks found</h2>
			<p className="text-sm text-gray-500 mb-4">Create your first task to get started</p>
			<Button onClick={onCreateClick}>
				<Plus className="w-4 h-4 mr-1" />
				New Task
			</Button>
		</div>
	)
}

// Column definitions, mirroring the main Tasks screen so the same columns sort
// the same way. The leading `dragHandle` slot reserves space for the grip column
// rendered by SortableTasksTableBody.
const headerCells = [
	{ id: 'dragHandle', label: '', sortable: false },
	{ id: 'completed', label: '', sortable: false },
	{ id: 'taskName', label: 'Task', sortable: true },
	{ id: 'assignedTo', label: 'Assigned', sortable: true },
	{ id: 'taskPriority', label: 'Priority', sortable: true },
	{ id: 'taskEffort', label: 'Effort', sortable: true },
	{ id: 'dateDue', label: 'Due Date', sortable: true },
	{ id: 'time', label: 'Time', sortable: false },
	{ id: 'actions', label: '', sortable: false },
]

// ─── Component ────────────────────────────────────────────────────────────────

const ProjectTasksCN = () => {
	const dispatch = useDispatch()
	const { id } = useParams()

	// Redux selectors
	const { userInfo } = useSelector((state: any) => state.userLogin)
	const { entities, ids, isLoading, isInitialized, selectedTask } = useSelector((state: any) => state.taskCNs)

	const { project } = useSelector((state: any) => ({
		project: state.projects.entities[id],
	}))
	const locationId = project?.projectAsset?._id || id
	const { location } = useSelector((state: any) => ({
		location: state.locations.entities[locationId],
	}))

	// Old tasks for template operations
	const {
		entities: taskEntities,
		ids: taskIds,
		isInitialized: tasksInitialized,
	} = useSelector((state: any) => state.tasks)
	const oldTasks = useMemo(
		() => (Array.isArray(taskIds) ? taskIds.map((tid: string) => taskEntities[tid]).filter(Boolean) : []),
		[taskIds, taskEntities]
	)
	const oldActiveTasks = useMemo(() => oldTasks.filter((t: any) => !t.completed), [oldTasks])

	// Templates
	const {
		entities: templateEntities,
		ids: templateIds,
		isInitialized: templatesInitialized,
	} = useSelector((state: any) => state.templates)
	const templates = useMemo(
		() => (Array.isArray(templateIds) ? templateIds.map((tid: string) => templateEntities[tid]).filter(Boolean) : []),
		[templateIds, templateEntities]
	)
	const taskTemplates = useMemo(() => templates.filter((t: any) => t.templateType === 'task'), [templates])

	// Local UI state
	const [viewMode, setViewMode] = useState<'table' | 'calendar' | 'timeline'>('table')
	const [showCreateModal, setShowCreateModal] = useState(false)
	const [userPopup, setUserPopup] = useState<{ user: any; anchor: DOMRect } | null>(null)
	const [openTemplatePopup, setOpenTemplatePopup] = useState(false)
	const [openBuildTemplatePopup, setOpenBuildTemplatePopup] = useState(false)
	const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false)
	const [duplicateMatches, setDuplicateMatches] = useState<{ name: string; id: string }[]>([])
	const [pendingTemplateId, setPendingTemplateId] = useState<string | null>(null)
	const [isDuplicateLoading, setIsDuplicateLoading] = useState(false)

	// Local filters (not shared with TasksCN_Index). No project filter — this
	// screen is already scoped to one project.
	const [searchQuery, setSearchQuery] = useState('')
	const [filterPriority, setFilterPriority] = useState('')
	const [filterAssignee, setFilterAssignee] = useState('')
	const [showCompleted, setShowCompleted] = useState(false)

	// Sorting state. Due Date descending matches the previous fixed sort.
	const [orderBy, setOrderBy] = useState<string | null>('dateDue')
	const [order, setOrder] = useState<'asc' | 'desc'>('desc')

	// Pagination state
	const [paginationEnabled, setPaginationEnabled] = useState(false)
	const [page, setPage] = useState(0)
	const [rowsPerPage, setRowsPerPage] = useState(10)

	const projectId = id!
	const userId = userInfo?._id

	// ─── Data fetching ──────────────────────────────────────────────────────────

	// Fetch TaskCN tasks for this project on mount
	useEffect(() => {
		if (userId && projectId) {
			dispatch(getTaskCNsByProject({ projectid: projectId, userid: userId }) as any)
		}
	}, [dispatch, userId, projectId])

	// Re-fetch when isInitialized goes false (after create/delete)
	useEffect(() => {
		if (userId && projectId && !isInitialized) {
			dispatch(getTaskCNsByProject({ projectid: projectId, userid: userId }) as any)
		}
	}, [dispatch, userId, projectId, isInitialized])

	// Fetch old tasks for template operations
	useEffect(() => {
		if (userId && locationId && projectId) {
			dispatch(initializeTasks({ userid: userId, packageid: locationId, projectid: projectId }) as any)
		}
	}, [dispatch, userId, locationId, projectId, tasksInitialized])

	// Fetch templates
	useEffect(() => {
		if (!templatesInitialized) {
			dispatch(initializeTemplates() as any)
		}
	}, [dispatch, templatesInitialized])

	// ─── TaskCN data ────────────────────────────────────────────────────────────

	const allTasks = useMemo(() => ids.map((tid: string) => entities[tid]).filter(Boolean), [ids, entities])

	// Assignee filter options, derived from the tasks in hand so the dropdown only
	// offers people who actually appear in this list.
	const assigneeOptions = useMemo(() => collectAssignees(allTasks), [allTasks])

	const filteredTasks = useMemo(
		() => filterTasks(allTasks, { searchQuery, filterPriority, filterAssignee, showCompleted }),
		[allTasks, searchQuery, filterPriority, filterAssignee, showCompleted]
	)

	const sortedTasks = useMemo(() => sortTaskList(filteredTasks, orderBy, order), [filteredTasks, orderBy, order])

	// Mobile lists are unpaginated and scrolled. Coerced for rendering and
	// never written back, so a preference set on a desktop survives a visit
	// from a phone.
	const isMobile = useIsMobile()
	const showPagination = paginationEnabled && !isMobile

	const paginatedTasks = useMemo(() => {
		if (!showPagination) return sortedTasks
		const start = page * rowsPerPage
		return sortedTasks.slice(start, start + rowsPerPage)
	}, [sortedTasks, page, rowsPerPage, showPagination])

	// Reset to the first page whenever the visible set or its order changes.
	useEffect(() => {
		setPage(0)
	}, [searchQuery, filterPriority, filterAssignee, showCompleted, orderBy, order, paginationEnabled])

	const handleSort = (columnId: string) => {
		if (orderBy === columnId) {
			setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setOrderBy(columnId)
			setOrder('asc')
		}
	}

	const hasAnyTasks = allTasks.length > 0

	// Map TaskCN tasks to old Task interface for Calendar/Timeline
	const tasksForCalendarTimeline = useMemo(
		() =>
			allTasks.map((t: any) => ({
				...t,
				completedBy: t.dateDue,
				taskStatus: t.completed ? 'Completed' : 'Open',
			})),
		[allTasks]
	)

	// ─── Handlers ───────────────────────────────────────────────────────────────

	// Completing a task is limited to its creator, its assignee and the owner of
	// the project or its location. See utils/taskPermissions.
	const canComplete = useCallback(
		(task: any) => canCompleteTask(task, userId, [project?.owner, location?.owner]),
		[userId, project, location]
	)

	const handleToggleComplete = useCallback(
		async (task: any) => {
			if (task.completed || !canComplete(task)) return
			const confirmed = await toast.confirm({
				title: 'Complete Task',
				message: `Mark "${task.taskName}" as completed?`,
				confirmLabel: 'Set as Completed',
				cancelLabel: 'Cancel',
				variant: 'default',
			})
			if (!confirmed) return
			const now = new Date().toISOString()
			dispatch(updateTaskCN({ _id: task._id, completed: true, dateCompleted: now }) as any)
		},
		[dispatch, canComplete]
	)

	const handleSelectTask = useCallback(
		(task: any) => {
			dispatch(setSelectedTask(task))
		},
		[dispatch]
	)

	const handleCloseDetail = useCallback(() => {
		dispatch(setSelectedTask(null))
	}, [dispatch])

	const handleAvatarClick = useCallback((e: React.MouseEvent, user: any) => {
		e.stopPropagation()
		const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
		setUserPopup({ user, anchor: rect })
	}, [])


	const handleTemplateLoad = async (templateId: string) => {
		// Check for soft-deleted duplicates before loading
		const checkResult = await dispatch(
			checkDeletedDuplicateTasks({ templateId, projectid: projectId }) as any,
		)

		const matches = checkResult.payload?.matches || []
		if (matches.length > 0) {
			setDuplicateMatches(matches.map((m: any) => ({ name: m.name, id: m.deletedItemId })))
			setPendingTemplateId(templateId)
			setDuplicateDialogOpen(true)
			return
		}

		// No duplicates — proceed normally
		const template = {
			templateId,
			packageid: locationId,
			projectid: projectId,
			packageType: location?.assetType,
		}
		await dispatch(loadTemplateCN(template) as any)
		setOpenTemplatePopup(false)
		dispatch(getTaskCNsByProject({ projectid: projectId, userid: userId }) as any)
		dispatch(patchLocation({ locationId, updates: { updateNote: 'New Task(s) Added' } }) as any)
	}

	const handleTemplateDuplicateRestore = async () => {
		if (!pendingTemplateId) return
		setIsDuplicateLoading(true)
		try {
			for (const match of duplicateMatches) {
				await dispatch(restoreTaskCN(match.id) as any)
			}
			const template = {
				templateId: pendingTemplateId,
				packageid: locationId,
				projectid: projectId,
				packageType: location?.assetType,
			}
			await dispatch(loadTemplateCN(template) as any)
			setDuplicateDialogOpen(false)
			setDuplicateMatches([])
			setPendingTemplateId(null)
			setOpenTemplatePopup(false)
			dispatch(getTaskCNsByProject({ projectid: projectId, userid: userId }) as any)
			dispatch(patchLocation({ locationId, updates: { updateNote: 'New Task(s) Added' } }) as any)
		} finally {
			setIsDuplicateLoading(false)
		}
	}

	const handleTemplateDuplicateCreateNew = async () => {
		if (!pendingTemplateId) return
		setIsDuplicateLoading(true)
		try {
			const template = {
				templateId: pendingTemplateId,
				packageid: locationId,
				projectid: projectId,
				packageType: location?.assetType,
			}
			await dispatch(loadTemplateCN(template) as any)
			setDuplicateDialogOpen(false)
			setDuplicateMatches([])
			setPendingTemplateId(null)
			setOpenTemplatePopup(false)
			dispatch(getTaskCNsByProject({ projectid: projectId, userid: userId }) as any)
			dispatch(patchLocation({ locationId, updates: { updateNote: 'New Task(s) Added' } }) as any)
		} finally {
			setIsDuplicateLoading(false)
		}
	}

	// Calendar/Timeline task click → open detail sheet
	const handleCalendarTimelineTaskClick = useCallback(
		(task: any) => {
			// Find the original TaskCN entity
			const taskCN = entities[task._id]
			if (taskCN) {
				dispatch(setSelectedTask(taskCN))
			}
		},
		[dispatch, entities]
	)

	const handleCalendarDateChange = useCallback(
		(task: any, newDate: string) => {
			dispatch(updateTaskCN({ _id: task._id, dateDue: newDate }) as any)
		},
		[dispatch]
	)

	// ─── Guard ──────────────────────────────────────────────────────────────────

	if (!project) return null

	// ─── Render ─────────────────────────────────────────────────────────────────

	return (
		<div>
			{/* Header */}
			<div className="flex flex-wrap justify-between items-center gap-4 mb-6">
				<div className="flex flex-col gap-1">
					<h2 className="text-2xl font-bold m-0">Tasks</h2>
					<span className="text-sm text-gray-600">Manage tasks, assignments, and due dates for this project.</span>
					<div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-0.5 mt-2 w-fit">
						<button
							type="button"
							onClick={() => setViewMode('table')}
							className={`flex items-center space-x-1 px-2.5 py-1.5 rounded-md transition-all text-xs ${
								viewMode === 'table' ? 'bg-white text-[#0080fe] shadow-sm' : 'text-gray-500 hover:text-[#0080fe]'
							}`}
							title="List View"
						>
							<Table2 className="w-3 h-3" />
							<span className="font-medium">List</span>
						</button>
						<button
							type="button"
							onClick={() => setViewMode('calendar')}
							className={`flex items-center space-x-1 px-2.5 py-1.5 rounded-md transition-all text-xs ${
								viewMode === 'calendar' ? 'bg-white text-[#0080fe] shadow-sm' : 'text-gray-500 hover:text-[#0080fe]'
							}`}
							title="Calendar View"
						>
							<Calendar className="w-3 h-3" />
							<span className="font-medium">Calendar</span>
						</button>
						<button
							type="button"
							onClick={() => setViewMode('timeline')}
							className={`flex items-center space-x-1 px-2.5 py-1.5 rounded-md transition-all text-xs ${
								viewMode === 'timeline' ? 'bg-white text-[#0080fe] shadow-sm' : 'text-gray-500 hover:text-[#0080fe]'
							}`}
							title="Timeline View"
						>
							<GitBranch className="w-3 h-3" />
							<span className="font-medium">Timeline</span>
						</button>
					</div>
				</div>
				{/* Create New Task and Download Template are always available:
				    an empty task list is exactly when you want to add the first
				    task or seed it from a template. */}
				<div className="flex items-center gap-3 flex-wrap">
					<Button onClick={() => setShowCreateModal(true)}>
						<Plus className="w-4 h-4 mr-1" />
						<span>Create New Task</span>
					</Button>
					<Button variant="ghost" onClick={() => setOpenTemplatePopup(true)}>
						<Download className="w-4 h-4 mr-1" />
						<span>Download Template</span>
					</Button>
					{/* Create Template builds a template FROM the tasks on this
					    page, so it stays hidden until there is at least one. */}
					{hasAnyTasks && (
						<Button variant="ghost" onClick={() => setOpenBuildTemplatePopup(true)}>
							<LayoutTemplate className="w-4 h-4 mr-1" />
							<span>Create Template</span>
						</Button>
					)}
				</div>
			</div>

			{viewMode === 'table' ? (
				<>
					{/* Filter Bar */}
					<div className="py-3 border-b border-gray-200 flex items-center gap-4 flex-wrap mb-4">
						<div className="relative flex-1 min-w-[200px] max-w-md">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
							<Input
								type="text"
								placeholder="Search tasks..."
								value={searchQuery}
								onChange={e => setSearchQuery(e.target.value)}
								className="pl-9"
							/>
						</div>
						<div className="flex items-center gap-2 flex-wrap">
							<Filter className="w-3.5 h-3.5 text-gray-400" />
							<Select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} className="w-[150px]">
								<option value="">All Priorities</option>
								<option value="P1 - Urgent">P1 - Urgent</option>
								<option value="P2 - High">P2 - High</option>
								<option value="P3 - Medium">P3 - Medium</option>
								<option value="P4 - Low">P4 - Low</option>
							</Select>
							<Select
								value={filterAssignee}
								onChange={e => setFilterAssignee(e.target.value)}
								className="w-[160px]"
							>
								<option value="">All Assignees</option>
								{assigneeOptions.map(a => (
									<option key={a.id} value={a.id}>
										{a.label}
									</option>
								))}
							</Select>
						</div>
						<div className="flex items-center gap-4 ml-auto">
							{/* Hidden on mobile, where lists are always unpaginated and this
							    switch would be a control that does nothing. */}
							{!isMobile && (
							<label className="flex items-center gap-2 text-sm text-gray-500 cursor-pointer">
								<Switch checked={paginationEnabled} onChange={e => setPaginationEnabled(e.target.checked)} />
								<span>Enable pagination</span>
							</label>
							)}
							<label className="flex items-center gap-2 text-sm text-gray-500 cursor-pointer">
								<Switch checked={showCompleted} onChange={e => setShowCompleted(e.target.checked)} />
								<span>Show completed</span>
							</label>
						</div>
					</div>

					{/* Table */}
					<div className="w-full">
						{isLoading ? (
							<div className="space-y-1">
								{[1, 2, 3, 4, 5, 6].map(i => (
									<div key={i} className="flex items-center gap-4 px-4 py-3 border-b border-gray-100 animate-pulse">
										<div className="w-5 h-5 rounded-full bg-gray-200" />
										<div className="flex-1 h-5 rounded bg-gray-200" />
										<div className="w-16 h-5 rounded bg-gray-200" />
										<div className="w-12 h-5 rounded bg-gray-200" />
										<div className="w-20 h-5 rounded bg-gray-200" />
									</div>
								))}
							</div>
						) : sortedTasks.length > 0 ? (
							<div className="rounded-lg border border-gray-200 overflow-hidden shadow-sm bg-white">
								<Table>
									<TableHeader>
										<TableRow className="bg-gray-50">
											{headerCells.map(cell => (
												<TableHead
													key={cell.id}
													className={cn(
														'text-gray-700 text-xs font-semibold whitespace-nowrap',
														cell.sortable && 'cursor-pointer select-none',
														cell.id === 'dragHandle' && 'w-8',
														cell.id === 'completed' && 'w-10',
														cell.id === 'taskName' && 'min-w-[200px]',
														cell.id === 'assignedTo' && 'w-10 text-center',
														cell.id === 'taskPriority' && 'w-24 text-center',
														cell.id === 'taskEffort' && 'w-20 text-center',
														cell.id === 'dateDue' && 'w-28 text-center',
														cell.id === 'time' && 'w-24 text-center',
														cell.id === 'actions' && 'w-10'
													)}
													onClick={cell.sortable ? () => handleSort(cell.id) : undefined}
												>
													<span className="inline-flex items-center">
														{cell.label}
														{cell.sortable && (
															<TableSortIcon columnId={cell.id} orderBy={orderBy} order={order} />
														)}
													</span>
												</TableHead>
											))}
										</TableRow>
									</TableHeader>
									<SortableTasksTableBody
										tasks={paginatedTasks}
										columnCount={headerCells.length}
										orderBy={orderBy}
										rowClassName={(task: any) =>
											`cursor-pointer group ${task.completed ? 'opacity-60' : ''} ${
												selectedTask?._id === task._id ? 'bg-blue-50' : ''
											}`
										}
										onRowClick={(task: any) => handleSelectTask(task)}
										onReorder={ids => dispatch(reorderTaskCNs(ids) as any)}
										renderRowCells={(task: any) => {
											const overdue = isOverdue(task.dateDue) && !task.completed
											const predecessor =
												typeof task.predecessorTaskId === 'object' ? task.predecessorTaskId : null
											return (
												<>
													<TableCell>
														<TaskCheckbox
															checked={task.completed}
															onChange={() => handleToggleComplete(task)}
															disabled={!canComplete(task)}
															title={
																canComplete(task)
																	? undefined
																	: 'Only the task creator, the assignee or the owner can complete this task'
															}
														/>
													</TableCell>
													<TableCell>
														<span
															className={`text-sm font-medium ${
																task.completed ? 'line-through text-gray-400' : 'text-gray-900'
															}`}
														>
															{task.taskName}
														</span>
														{task.description && (
															<span className="text-sm text-gray-500 ml-1">- {task.description}</span>
														)}
														{predecessor?.taskName && (
															<span className="ml-2 inline-flex items-center gap-1 text-[11px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
																<ArrowRight className="w-3 h-3" />
																<span title={predecessor.taskName}>follows {predecessor.taskName}</span>
															</span>
														)}
														{task.completed && task.dateCompleted && (
															<span className="ml-2 text-xs text-gray-400">
																Date Completed: {new Date(task.dateCompleted).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}
															</span>
														)}
													</TableCell>
													<TableCell className="text-center">
														<button
															type="button"
															className="bg-transparent border-none p-0 m-0 cursor-pointer mx-auto block"
															onClick={e => handleAvatarClick(e, task.assignedTo)}
															title={task.assignedTo?.name || task.assignedTo?.firstName || ''}
														>
															<Avatar className="h-7 w-7">
																<AvatarImage src={task.assignedTo?.image} alt={task.assignedTo?.name || ''} />
																<AvatarFallback className="text-xs bg-gray-200 text-gray-600">
																	{(task.assignedTo?.name || task.assignedTo?.firstName || '?').charAt(0).toUpperCase()}
																</AvatarFallback>
															</Avatar>
														</button>
													</TableCell>
													<TableCell className="text-center">
														<PriorityBadge priority={task.taskPriority} />
													</TableCell>
													<TableCell className="text-center">
														<EffortBadge effort={task.taskEffort} />
													</TableCell>
													<TableCell className="text-center">
														<span
															className={`text-sm inline-flex items-center gap-1 ${
																overdue ? 'text-red-500' : 'text-gray-500'
															}`}
														>
															{task.dateDue && <Calendar className="w-3 h-3" />}
															{formatDueDate(task.dateDue)}
														</span>
													</TableCell>
													{/* Time: its own column, filled only for a scheduled time (MYD-534) */}
													<TableCell className="text-center text-sm text-gray-500 whitespace-nowrap">
														{task.hasScheduledTime ? formatScheduledTime(task.dateDue) : ''}
													</TableCell>
													<TableCell>
														<button
															className="p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-[#0080fe]"
															onClick={e => {
																e.stopPropagation()
																handleSelectTask(task)
															}}
															title="View details"
														>
															<Eye className="w-4 h-4" />
														</button>
													</TableCell>
												</>
											)
										}}
									/>
								</Table>
								{showPagination && sortedTasks.length > 10 && (
									<TablePagination
										page={page}
										rowsPerPage={rowsPerPage}
										count={sortedTasks.length}
										onPageChange={setPage}
										onRowsPerPageChange={setRowsPerPage}
									/>
								)}
							</div>
						) : (
							<EmptyState onCreateClick={() => setShowCreateModal(true)} />
						)}
					</div>
				</>
			) : viewMode === 'timeline' ? (
				<div style={{ width: '100%', maxWidth: '100%' }}>
					<TasksTimeline
						tasks={tasksForCalendarTimeline}
						onTaskClick={handleCalendarTimelineTaskClick}
						onTaskDateChange={handleCalendarDateChange}
					/>
				</div>
			) : (
				<div style={{ width: '100%', maxWidth: '100%' }}>
					<TasksCalendar
						tasks={tasksForCalendarTimeline}
						onTaskClick={handleCalendarTimelineTaskClick}
						onTaskDateChange={handleCalendarDateChange}
					/>
				</div>
			)}

			{/* Create Task Modal */}
			<CreateTaskCNModal
				open={showCreateModal}
				onClose={() => setShowCreateModal(false)}
				defaultProjectId={projectId}
			/>

			{/* Task Detail Sheet */}
			<TaskCNDetailSheet task={selectedTask} open={!!selectedTask} onClose={handleCloseDetail} />

			{/* User Info Popup */}
			{userPopup && (
				<AssigneePopover user={userPopup.user} anchor={userPopup.anchor} onClose={() => setUserPopup(null)} />
			)}

			{/* Download Template Popup (old task system) */}
			{openTemplatePopup && (
				<Popup
					title="Task Templates"
					openPopup={openTemplatePopup}
					setOpenPopup={setOpenTemplatePopup}
					icon={<LayoutTemplate className="w-5 h-5" />}
					description="Browse and apply task templates"
				>
					<TaskTemplatesSelectCN taskTemplates={taskTemplates} handleTamplateLoad={handleTemplateLoad} />
				</Popup>
			)}

			{/* Tasks Template Popup */}
			{openBuildTemplatePopup && (
				<Popup
					title="Tasks Template"
					openPopup={openBuildTemplatePopup}
					setOpenPopup={setOpenBuildTemplatePopup}
					popupWidth="lg"
					icon={<LayoutTemplate className="w-5 h-5" />}
					description="Create a new task template"
				>
					<BuildTemplateModal
						tasks={allTasks}
						openPopup={openBuildTemplatePopup}
						setOpenPopup={setOpenBuildTemplatePopup}
						onBuildTemplate={async (templateData: any) => {
							try {
								await dispatch(buildTemplateFromTasks(templateData) as any)
								setOpenBuildTemplatePopup(false)
								dispatch(initializeTemplates() as any)
								notify({
									title: 'Template Created!',
									text: `The template "${templateData.templateName}" has been created successfully.`,
									icon: 'success',
								})
							} catch (error: any) {
								notify({
									title: 'Error',
									text: error.message || 'Failed to create template',
									icon: 'error',
								})
							}
						}}
					/>
				</Popup>
			)}
		{duplicateMatches.length > 0 && (
				<DeletedDuplicateDialog
					open={duplicateDialogOpen}
					onClose={() => {
						setDuplicateDialogOpen(false)
						setDuplicateMatches([])
						setPendingTemplateId(null)
					}}
					itemType="task"
					matches={duplicateMatches}
					onRestore={handleTemplateDuplicateRestore}
					onCreateNew={handleTemplateDuplicateCreateNew}
					isLoading={isDuplicateLoading}
				/>
			)}
		</div>
	)
}

export default ProjectTasksCN
